// CycleTrackerPreview.jsx
// Sandbox preview — no auth, no API. Access at /CycleTrackerPreview
// Renders the real CycleTracker with mock cycle data (same pattern as
// /DesignSystem and /HormoneAddonPreview) so the redesign can be checked
// visually without needing a login.

import { useContext, useState } from "react";
import dayjs from "dayjs";
import { Box, Button, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ColorModeContext } from "../../../../theme";
import { CycleDataContext } from "../CycleDataContext";
import CycleTracker from "./CycleTracker";
import CycleAdministration from "./CycleAdministration";

function buildMockCycleData() {
  const today = dayjs().startOf("day");
  const cycleStart = today.subtract(13, "day"); // → today = cycle day 14, inside ovulation

  const range = (offsetStart, offsetEnd, from) => ({
    start: from.add(offsetStart, "day"),
    end: from.add(offsetEnd, "day"),
  });

  function makeCycle({ from, isPrediction }) {
    const menstrual  = range(0, 4, from);
    const follicular = range(5, 12, from);
    const ovulation  = range(13, 15, from);
    const luteal     = range(16, 27, from);
    return {
      is_active: !isPrediction,
      is_system_prediction: isPrediction,
      cycle_start_date: from,
      average_cycle_length: 28,
      period_length: 5,
      ovulation_start_day: 14,
      menstrual_start_date: menstrual.start,   menstrual_end_date: menstrual.end,
      follicular_start_date: follicular.start, follicular_end_date: follicular.end,
      ovulation_start_date: ovulation.start,   ovulation_end_date: ovulation.end,
      luteal_start_date: luteal.start,         luteal_end_date: luteal.end,
    };
  }

  const activeCycle = makeCycle({ from: cycleStart, isPrediction: false });
  const pastCycle = { ...makeCycle({ from: cycleStart.subtract(28, "day"), isPrediction: false }), id: "mock-past", is_active: false, current_cycle_length: 28 };
  activeCycle.id = "mock-active";
  const predictedCycle = makeCycle({ from: cycleStart.add(28, "day"), isPrediction: true });
  predictedCycle.id = "mock-predicted";
  const cycles = [activeCycle, pastCycle];
  const predictedCycles = [predictedCycle];

  const symptoms = [
    { id: "s1", date: cycleStart.add(1, "day"), category: "physical", symptom_name: "Cramps", severity: 2, notes: "" },
    { id: "s2", date: cycleStart.add(13, "day"), category: "energy", symptom_name: "High energy", severity: 1, notes: "" },
  ];

  const matchPhase = (c, date) => {
    if (!date.isBefore(c.menstrual_start_date) && !date.isAfter(c.menstrual_end_date))
      return { phase: "menstrual", isPredicted: c.is_system_prediction };
    if (!date.isBefore(c.follicular_start_date) && !date.isAfter(c.follicular_end_date))
      return { phase: "follicular", isPredicted: c.is_system_prediction };
    if (!date.isBefore(c.ovulation_start_date) && !date.isAfter(c.ovulation_end_date))
      return { phase: "ovulation", isPredicted: c.is_system_prediction };
    if (!date.isBefore(c.luteal_start_date) && !date.isAfter(c.luteal_end_date))
      return { phase: "luteal", isPredicted: c.is_system_prediction };
    return null;
  };

  const getPhaseForDate = (date) => {
    if (!date) return null;
    for (const c of cycles) { const r = matchPhase(c, date); if (r) return r; }
    for (const c of predictedCycles) { const r = matchPhase(c, date); if (r) return r; }
    return null;
  };

  return {
    loading: false,
    activeCycle,
    cycles,
    predictedCycles,
    symptoms,
    symptomsLoading: false,
    statistics: { average_cycle_length: 28, average_period_length: 5, average_ovulation_day: 14 },
    loadAll: async () => {},
    loadSymptoms: async () => {},
    getPhaseForDate,
  };
}

export default function CycleTrackerPreview() {
  const mockData = buildMockCycleData();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const [tab, setTab] = useState("tracker");
  return (
    <Box sx={{
      minHeight: "100vh",
      bgcolor: "background.default",
      py: 4,
    }}>
      <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 2, sm: 4 } }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button size="small" onClick={colorMode.toggleColorMode}>
            Toggle {theme.palette.mode === "dark" ? "light" : "dark"} mode
          </Button>
          <Button size="small" variant={tab === "tracker" ? "contained" : "text"} onClick={() => setTab("tracker")}>
            Tracker
          </Button>
          <Button size="small" variant={tab === "admin" ? "contained" : "text"} onClick={() => setTab("admin")}>
            Administration
          </Button>
        </Stack>
        <CycleDataContext.Provider value={mockData}>
          {tab === "tracker" ? <CycleTracker /> : <CycleAdministration />}
        </CycleDataContext.Provider>
      </Box>
    </Box>
  );
}
